/**
 * DecisionFlow — VolumeManager
 * ui/src/components/tenant-admin/VolumeManager.jsx
 *
 * Domain: §P4-12 Volume / Unit Rate
 * Embedded in: PlanDetailView modifier card grid (Pattern A)
 * API:
 *   GET  /api/v1/unit-quotas?plan_component_id={id}
 *   POST /api/v1/unit-quotas
 *   POST /api/v1/unit-quotas/{id}/approve
 * Feature gate: Standard — no gate check needed.
 * tenant_super_admin excluded (NN-36).
 * State derived from approved_by / submitted_at — no status column.
 * planComponentId prop is required — all API calls scoped to it.
 * Path: ui/src/components/tenant-admin/VolumeManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens (non-negotiables)
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  unit_type:         '',
  rate_per_unit:     '',
  quota_units:       '',
  effective_date:    '',
  expiration_date:   '',
};

// ---------------------------------------------------------------------------
// VolumeManager
// ---------------------------------------------------------------------------

export default function VolumeManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.volume')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [items, setItems]                   = useState([]);
  const [listLoading, setListLoading]       = useState(false);
  const [listError, setListError]           = useState('');
  const [selectedItem, setSelectedItem]     = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [saving, setSaving]                 = useState(false);
  const [formError, setFormError]           = useState('');
  const [actionLoading, setActionLoading]   = useState(false);
  const [actionError, setActionError]       = useState('');
  const [toast, setToast]                   = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------

  const loadItems = useCallback(async () => {
    if (!user || !planComponentId) return;
    setListLoading(true);
    setListError('');
    try {
      const data = await apiGet(`/api/v1/unit-quotas?plan_component_id=${planComponentId}`);
      setItems(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      setListError(getErrorMessage(error, 'Unable to load volume quotas.'));
    } finally {
      setListLoading(false);
    }
  }, [user, planComponentId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.unit_type.trim() || !form.rate_per_unit) {
      setFormError('Unit type and rate per unit are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await apiPost('/api/v1/unit-quotas', {
        plan_component_id: planComponentId,
        unit_type:         form.unit_type.trim(),
        rate_per_unit:     parseFloat(form.rate_per_unit),
        quota_units:       form.quota_units ? parseFloat(form.quota_units) : null,
        effective_date:    form.effective_date || null,
        expiration_date:   form.expiration_date || null,
      });
      setShowCreateForm(false);
      setForm(EMPTY_FORM);
      showToast('Volume quota created.');
      await loadItems();
    } catch (error) {
      setFormError(getErrorMessage(error, 'Failed to create volume quota.'));
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Submit for approval
  // ---------------------------------------------------------------------------

  async function handleSubmit() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/unit-quotas/${selectedItem.unit_quota_id}/submit`);
      showToast('Submitted for approval.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setActionError(getErrorMessage(error, 'Failed to submit for approval.'));
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Approve
  // ---------------------------------------------------------------------------

  async function handleApprove() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/unit-quotas/${selectedItem.unit_quota_id}/approve`);
      showToast('Volume quota approved.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setActionError('Self-approval is not permitted (NN-10).');
      } else {
        setActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Detail view
  // ---------------------------------------------------------------------------

  if (selectedItem) {
    const state = deriveState(selectedItem);
    return (
      <section className="flex flex-col gap-4">
        <button type="button" onClick={() => { setSelectedItem(null); setActionError(''); }} className={btnSecondary + ' w-fit'}>
          ← Back to list
        </button>

        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
              {selectedItem.unit_type}
            </h2>
            <StatusPill status={state} />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Rate / Unit</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.rate_per_unit != null ? Number(selectedItem.rate_per_unit).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Quota Units</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.quota_units != null ? Number(selectedItem.quota_units).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.effective_date ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Expires</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.expiration_date ?? '—'}
              </p>
            </div>
          </div>

          {actionError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {actionError}
            </div>
          )}
        </div>

        {!readOnly && (
          <ApprovalPanel
            variant="volume_quota"
            currentState={state}
            submittedBy={selectedItem.submitted_by}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? handleSubmit : null}
            onApprove={state === 'pending_approval' ? handleApprove : null}
            onReject={null}
            approvedBy={selectedItem.approved_by}
            approvedAt={selectedItem.approved_at}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">

      {/* Read-only banner */}
      {readOnly && (
        <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Plan is approved — configuration is read-only.
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Volume / Unit Quotas</h2>
        {!readOnly && (
          <button
            type="button"
            onClick={() => { setShowCreateForm((v) => !v); setFormError(''); setForm(EMPTY_FORM); }}
            className={btnPrimary}
          >
            {showCreateForm ? 'Cancel' : '+ Add Quota'}
          </button>
        )}
      </div>

      {/* Create form */}
      {showCreateForm && (
        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
        >
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Volume Quota</p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={labelClass}>
              Unit Type <span className="text-[rgb(var(--color-danger))]">*</span>
              <input
                type="text"
                value={form.unit_type}
                onChange={(e) => setForm((f) => ({ ...f, unit_type: e.target.value }))}
                placeholder="e.g. license, seat, unit"
                className={inputClass}
                required
              />
            </label>

            <label className={labelClass}>
              Rate per Unit <span className="text-[rgb(var(--color-danger))]">*</span>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={form.rate_per_unit}
                onChange={(e) => setForm((f) => ({ ...f, rate_per_unit: e.target.value }))}
                placeholder="0.00"
                className={inputClass}
                required
              />
            </label>

            <label className={labelClass}>
              Quota Units
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.quota_units}
                onChange={(e) => setForm((f) => ({ ...f, quota_units: e.target.value }))}
                placeholder="0"
                className={inputClass}
              />
            </label>

            <label className={labelClass}>
              Effective Date
              <input
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                className={inputClass}
              />
            </label>

            <label className={labelClass}>
              Expiration Date
              <input
                type="date"
                value={form.expiration_date}
                onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))}
                className={inputClass}
              />
            </label>
          </div>

          {formError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {formError}
            </div>
          )}

          <div className="flex gap-2">
            <button type="submit" disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : 'Create Quota'}
            </button>
            <button
              type="button"
              onClick={() => { setShowCreateForm(false); setFormError(''); setForm(EMPTY_FORM); }}
              className={btnSecondary}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {listLoading && (
        <div className="animate-pulse space-y-2">
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
        </div>
      )}

      {listError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {listError}
        </div>
      )}

      {!listLoading && !listError && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No volume quotas configured</p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Add a quota to set unit-based compensation rates for this component.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Unit Type</th>
                <th className={thClass}>Rate / Unit</th>
                <th className={thClass}>Quota Units</th>
                <th className={thClass}>Effective</th>
                <th className={thClass}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.unit_quota_id}
                  className="cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                  onClick={() => { setSelectedItem(item); setActionError(''); }}
                >
                  <td className={tdClass + ' font-medium'}>{item.unit_type}</td>
                  <td className={tdClass}>{item.rate_per_unit != null ? Number(item.rate_per_unit).toLocaleString() : '—'}</td>
                  <td className={tdClass}>{item.quota_units != null ? Number(item.quota_units).toLocaleString() : '—'}</td>
                  <td className={tdClass}>{item.effective_date ?? '—'}</td>
                  <td className={tdClass}>
                    <StatusPill status={deriveState(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          role="status"
          className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg"
        >
          {toast}
        </div>
      )}
    </section>
  );
}
